import classNames from "classnames";
import React, { FunctionComponent, useCallback, useMemo, useState } from "react";
import { ContextMenu, MenuItemRenderer } from "../context-menu";
import { Drawer } from "../drawer/drawer";
import { Text } from "../text/text";
import { CSS_CLASSES } from "./global-header-css-classes";
import { GlobalHeaderUserMenuButton, GlobalHeaderUserMenuButtonProps } from "./global-header-user-menu-button";

export interface GlobalHeaderUserMenuItem {
  /**
   * Label of the use menu item link.
   */
  label: string;

  /**
   * Accepts a handler function for the "click" event on the user menu item link.
   */
  onClick: () => void;
}

export interface GlobalHeaderUserMenuProps {
  /**
   * The visual variant of the use menu.
   */
  variant?: "dark" | "light";

  /**
   * Color option for the user menu.
   */
  color?: "interactive" | "primary";

  /**
   * Logged in user's first name.
   * Used for the Avatar component.
   */
  firstName: string;

  /**
   * Logged in user's last name.
   * Used for the Avatar component.
   */
  lastName: string;

  /**
   * Menu items.
   */
  menuItems: GlobalHeaderUserMenuItem[];

  /**
   * Controls whether or not to apply a top offset, equal to the height of the utility bar, to the user menu.
   */
  addTopOffsetToMobileMenu?: boolean;

  /**
   * Apply arbitrary CSS classes to a particular menu item for fine grained control on
   * visual styling. Use with caution.
   */
  className?: string;

  isDesktop: boolean;
}

export const GlobalHeaderUserMenu: FunctionComponent<GlobalHeaderUserMenuProps> = ({
  variant = "light",
  color = "interactive",
  firstName,
  lastName,
  menuItems,
  addTopOffsetToMobileMenu = false,
  className,
  isDesktop,
}) => {
  const rootClassName = useMemo(() => classNames(CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ROOT, className), [className]);

  const menuItemsToRender = useMemo(
    () => [{ label: "Close Menu", onClick: () => {} }, ...menuItems, { label: "", onClick: () => {}, disabled: true }],
    [menuItems]
  );

  const menuButtonProps: GlobalHeaderUserMenuButtonProps = useMemo(
    () => ({
      variant,
      color,
      isDesktop,
      firstName,
      lastName,
    }),
    [color, firstName, isDesktop, lastName, variant]
  );

  const contextMenuItemRenderer: MenuItemRenderer = useCallback(
    ({ item, index, highlighted }) => {
      const menuItemsLength: number = menuItems.length;
      if (index === 0) {
        // First item in the list is a placeholder for the menu button.
        return (
          <div
            className={classNames(CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_USER_MENU_BUTTON, {
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_HIGHLIGHTED]: highlighted,
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_PRIMARY]: color === "primary",
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_INTERACTIVE]: color === "interactive",
            })}
          >
            <GlobalHeaderUserMenuButton {...menuButtonProps} menuOpen={true} variant="dark" />
          </div>
        );
      } else if (index === menuItemsLength + 1) {
        // Last item in the list is a placeholder for a spacer, only rendered in the desktop view.
        return <div className={CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_END_SPACER}></div>;
      } else {
        return (
          <MenuItem
            label={item.label}
            color={color}
            className={classNames(CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM, {
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_HIGHLIGHTED]: highlighted,
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_PRIMARY]: color === "primary",
              [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_INTERACTIVE]: color === "interactive",
            })}
          />
        );
      }
    },
    [color, menuButtonProps, menuItems.length]
  );

  const [drawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const mobileMenuButtonProps = useMemo(
    () => ({ ...menuButtonProps, onClick: openDrawer }),
    [menuButtonProps, openDrawer]
  );

  const handleMobileMenuItemClick = useCallback(
    (menuItemOnClick) => {
      menuItemOnClick?.();
      closeDrawer();
    },
    [closeDrawer]
  );

  const mobileMenuDrawerContent = (
    <div
      className={classNames(CSS_CLASSES.GLOBAL_HEADER_USER_MENU_DRAWER_CONTENT, {
        [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_DRAWER_CONTENT_OFFSET_DARK]:
          addTopOffsetToMobileMenu && variant === "dark",
        [CSS_CLASSES.GLOBAL_HEADER_USER_MENU_DRAWER_CONTENT_OFFSET_LIGHT]:
          addTopOffsetToMobileMenu && variant === "light",
      })}
    >
      <div className={CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_USER_MENU_BUTTON_MOBILE} onClick={closeDrawer}>
        <GlobalHeaderUserMenuButton {...mobileMenuButtonProps} menuOpen={true} variant="dark" />
      </div>
      {menuItems.map((item) => (
        <MenuItem
          key={item.label}
          label={item.label}
          color={color}
          onClick={() => handleMobileMenuItemClick(item.onClick)}
          className={CSS_CLASSES.GLOBAL_HEADER_USER_MENU_ITEM_MOBILE}
        />
      ))}
    </div>
  );

  return (
    <div className={rootClassName}>
      {isDesktop ? (
        <ContextMenu
          menuItems={menuItemsToRender}
          menuItemRenderer={contextMenuItemRenderer}
          placement="bottom-end"
          menuSizeStyle={{ minWidth: 150, maxWidth: 320 }}
          menuClassName="rounded-0"
          popoverModifiers={[
            {
              name: "offset",
              options: {
                offset: [24, -63],
              },
            },
          ]}
        >
          {({ open, toggleButtonProps }) => (
            <GlobalHeaderUserMenuButton {...menuButtonProps} menuOpen={open} {...toggleButtonProps} />
          )}
        </ContextMenu>
      ) : (
        <>
          <GlobalHeaderUserMenuButton {...mobileMenuButtonProps} menuOpen={false} />
          <Drawer
            open={drawerOpen}
            slideFrom="right"
            slidingContent={mobileMenuDrawerContent}
            showBackdrop={false}
            onClose={closeDrawer}
          />
        </>
      )}
    </div>
  );
};

/**
 * Internal helper component to render a Menu Item.
 */
const MenuItem = ({
  label,
  color,
  onClick,
  className,
  ...restProps
}: {
  label: string;
  color: "interactive" | "primary";
  onClick?: () => void;
  className?: string;
}) => (
  <div className={className} onClick={onClick} {...restProps}>
    <Text variant="heading-05" color={color}>
      {label}
    </Text>
  </div>
);
